import { useState, useEffect, useMemo } from 'react';

export const useResearchData = () => {
  const [researchData, setResearchData] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    institution: '',
    year: '',
    outputType: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch research data
        const researchResponse = await fetch('/data/research_data.json');
        if (!researchResponse.ok) {
          throw new Error(`HTTP error! status: ${researchResponse.status}`);
        }
        const researchText = await researchResponse.text();
        let research;
        try {
          research = JSON.parse(researchText);
        } catch (e) {
          console.error('Research data parsing error:', researchText.slice(0, 200));
          throw new Error('Failed to parse research data');
        }

        // Fetch analytics data
        const analyticsResponse = await fetch('/data/analytics_data.json');
        if (!analyticsResponse.ok) {
          throw new Error(`HTTP error! status: ${analyticsResponse.status}`);
        }
        const analyticsText = await analyticsResponse.text();
        let analytics;
        try {
          analytics = JSON.parse(analyticsText);
        } catch (e) {
          console.error('Analytics data parsing error:', analyticsText.slice(0, 200));
          throw new Error('Failed to parse analytics data');
        }

        // Validate data structure
        if (!Array.isArray(research)) {
          throw new Error('Research data is not an array');
        }

        setResearchData(research);
        setAnalyticsData(analytics);
      } catch (err) {
        console.error('Data loading error:', err);
        setError(err.message);
        setResearchData([]);
        setAnalyticsData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    if (!researchData.length) return [];

    return researchData.filter(item => {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = !filters.search || 
        Object.values(item).some(val => 
          val && val.toString().toLowerCase().includes(searchLower)
        );

      const matchesInstitution = !filters.institution || 
        item.institution === filters.institution;

      const matchesYear = !filters.year || 
        (item.year && item.year === parseInt(filters.year));

      const matchesType = !filters.outputType || 
        item.outputType === filters.outputType;

      return matchesSearch && matchesInstitution && matchesYear && matchesType;
    });
  }, [researchData, filters]);

  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  return {
    data: filteredData,
    analyticsData,
    loading,
    error,
    filters,
    updateFilters,
    totalRecords: researchData.length
  };
};